import { gql, TypedDocumentNode } from "@apollo/client";
import {
  AsyncTaskStatus,
  Mutation,
  MutationDeleteClientShareClassArgs,
  MutationImportClientPortfolioReturnsArgs,
  MutationSaveClientCashFlowArgs,
  MutationSaveClientMonthEntriesArgs,
  MutationSaveClientShareClassArgs,
  MutationSaveClientSwitchArgs,
  Query,
  QueryAsyncTaskStatusArgs,
  QueryClientAllocationsArgs,
  QueryClientCapitalFlowsArgs,
  QueryClientInvestmentClientsArgs,
  QueryClientShareClassesArgs,
  QueryClientSwitchesArgs,
} from "_graphql-types";

// Every query selects every field of its row type, so Pick<Query, ...> is the exact result type.

export const CLIENT_INVESTMENT_FUNDS: TypedDocumentNode<Pick<Query, "clientInvestmentFunds">> = gql`
  query ClientInvestmentFunds {
    clientInvestmentFunds {
      rcgFundId
      name
      groupId
      groupName
      isCombined
    }
  }
`;

export const CLIENT_INVESTMENT_CLIENTS: TypedDocumentNode<
  Pick<Query, "clientInvestmentClients">,
  QueryClientInvestmentClientsArgs
> = gql`
  query ClientInvestmentClients($rcgFundId: Int) {
    clientInvestmentClients(rcgFundId: $rcgFundId) {
      clientId
      name
    }
  }
`;

export const CLIENT_FEE_OPTIONS: TypedDocumentNode<Pick<Query, "clientFeeOptions">> = gql`
  query ClientFeeOptions {
    clientFeeOptions {
      aum {
        id
        label
      }
      rate {
        id
        label
      }
      unit {
        id
        label
      }
    }
  }
`;

export const CLIENT_SHARE_CLASSES: TypedDocumentNode<
  Pick<Query, "clientShareClasses">,
  QueryClientShareClassesArgs
> = gql`
  query ClientShareClasses($rcgFundId: Int!, $clientId: Int) {
    clientShareClasses(rcgFundId: $rcgFundId, clientId: $clientId) {
      shareClassId
      name
      rcgFundId
      clientId
      unfunded
      feeAumOptionId
      feeRateOptionId
      feeUnitOptionId
      mgmtTiers {
        threshold
        rate
        isFlat
      }
      adminTiers {
        threshold
        rate
        isFlat
      }
      deletable
      derived
    }
  }
`;

export const CLIENT_CAPITAL_FLOWS: TypedDocumentNode<
  Pick<Query, "clientCapitalFlows">,
  QueryClientCapitalFlowsArgs
> = gql`
  query ClientCapitalFlows($rcgFundId: Int!, $clientId: Int!) {
    clientCapitalFlows(rcgFundId: $rcgFundId, clientId: $clientId) {
      shareClassId
      date
      addition
      redemption
      switchIn
      switchOut
    }
  }
`;

export const CLIENT_SWITCHES: TypedDocumentNode<Pick<Query, "clientSwitches">, QueryClientSwitchesArgs> = gql`
  query ClientSwitches($rcgFundId: Int!, $clientId: Int!) {
    clientSwitches(rcgFundId: $rcgFundId, clientId: $clientId) {
      fromShareClassId
      toShareClassId
      date
      amount
    }
  }
`;

export const CLIENT_ALLOCATIONS: TypedDocumentNode<
  Pick<Query, "clientAllocations">,
  QueryClientAllocationsArgs
> = gql`
  query ClientAllocations($rcgFundIds: [Int!], $shareClassId: Int, $month: Date, $withFlowsOnly: Boolean! = false) {
    clientAllocations(rcgFundIds: $rcgFundIds, shareClassId: $shareClassId, month: $month, withFlowsOnly: $withFlowsOnly) {
      shareClassId
      shareClassName
      rcgFundId
      rcgFundName
      clientId
      clientName
      derived
      date
      begBal
      addition
      redemption
      switchIn
      switchOut
      adjBegBal
      mgmtFeeRate
      mgmtFee
      adminFeeRate
      adminFee
      incentiveFee
      estGrossReturn
      estNetReturn
      estGrossEndBal
      estNetEndBal
      actGrossReturn
      actNetReturn
      actGrossEndBal
      actNetEndBal
      finalGrossReturn
      finalNetReturn
      effGrossReturn
      effNetReturn
      grossEndBal
      netEndBal
      pl
      enteredMgmtFee
      enteredAdminFee
      enteredIncentiveFee
    }
  }
`;

export const ASYNC_TASK_STATUS: TypedDocumentNode<
  { asyncTaskStatus?: Pick<AsyncTaskStatus, "id" | "endDate" | "error"> | null },
  QueryAsyncTaskStatusArgs
> = gql`
  query ClientInvestmentsAsyncTaskStatus($id: ID!) {
    asyncTaskStatus(id: $id) {
      id
      endDate
      error
    }
  }
`;

export const SAVE_CLIENT_SHARE_CLASS: TypedDocumentNode<
  Pick<Mutation, "saveClientShareClass">,
  MutationSaveClientShareClassArgs
> = gql`
  mutation SaveClientShareClass($input: ClientShareClassInput!) {
    saveClientShareClass(input: $input)
  }
`;

export const DELETE_CLIENT_SHARE_CLASS: TypedDocumentNode<
  Pick<Mutation, "deleteClientShareClass">,
  MutationDeleteClientShareClassArgs
> = gql`
  mutation DeleteClientShareClass($shareClassId: Int!) {
    deleteClientShareClass(shareClassId: $shareClassId)
  }
`;

export const SAVE_CLIENT_CASH_FLOW: TypedDocumentNode<
  Pick<Mutation, "saveClientCashFlow">,
  MutationSaveClientCashFlowArgs
> = gql`
  mutation SaveClientCashFlow($shareClassId: Int!, $month: Date!, $addition: Float, $redemption: Float) {
    saveClientCashFlow(shareClassId: $shareClassId, month: $month, addition: $addition, redemption: $redemption)
  }
`;

export const SAVE_CLIENT_SWITCH: TypedDocumentNode<
  Pick<Mutation, "saveClientSwitch">,
  MutationSaveClientSwitchArgs
> = gql`
  mutation SaveClientSwitch($fromShareClassId: Int!, $toShareClassId: Int!, $month: Date!, $amount: Float!) {
    saveClientSwitch(fromShareClassId: $fromShareClassId, toShareClassId: $toShareClassId, month: $month, amount: $amount)
  }
`;

export const SAVE_CLIENT_MONTH_ENTRIES: TypedDocumentNode<
  Pick<Mutation, "saveClientMonthEntries">,
  MutationSaveClientMonthEntriesArgs
> = gql`
  mutation SaveClientMonthEntries($month: Date!, $entries: [ClientMonthEntryInput!]!) {
    saveClientMonthEntries(month: $month, entries: $entries)
  }
`;

export const IMPORT_CLIENT_PORTFOLIO_RETURNS: TypedDocumentNode<
  { importClientPortfolioReturns: Pick<AsyncTaskStatus, "id" | "label" | "startDate"> },
  MutationImportClientPortfolioReturnsArgs
> = gql`
  mutation ImportClientPortfolioReturns($rcgFundIds: [Int!], $start: Date) {
    importClientPortfolioReturns(rcgFundIds: $rcgFundIds, start: $start) {
      id
      label
      startDate
    }
  }
`;
